import { useState, useRef, useEffect, useCallback } from 'react'
import { useDevToolsStore, type ConsoleLevel } from '../store/devtools-store'
import { JsonTree } from './json-tree'

function formatTime(ts: number): string {
  const d = new Date(ts)
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
    + '.' + String(d.getMilliseconds()).padStart(3, '0')
}

const LEVEL_FILTERS: Array<{ id: ConsoleLevel | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'info', label: 'Info' },
  { id: 'warn', label: 'Warn' },
  { id: 'error', label: 'Error' },
]

function LevelIcon({ level }: { level: ConsoleLevel }) {
  switch (level) {
    case 'info':
      return <span className="w-3 h-3 flex items-center justify-center text-muted-foreground shrink-0"><span className="w-1.5 h-1.5 rounded-full border border-current" /></span>
    case 'warn':
      return <span className="w-3 h-3 flex items-center justify-center text-yellow-400 shrink-0 text-[10px] leading-none">&#9650;</span>
    case 'error':
      return <span className="w-3 h-3 flex items-center justify-center text-red-400 shrink-0"><span className="w-1.5 h-1.5 rounded-full bg-current" /></span>
  }
}

function levelTextColor(level: ConsoleLevel): string {
  switch (level) {
    case 'info': return 'text-muted-foreground'
    case 'warn': return 'text-yellow-400'
    case 'error': return 'text-red-400'
  }
}

export function ConsoleTab() {
  const consoleLog = useDevToolsStore((s) => s.consoleLog)
  const clearConsoleLog = useDevToolsStore((s) => s.clearConsoleLog)
  const runtime = useDevToolsStore((s) => s.runtime)
  const [filter, setFilter] = useState<ConsoleLevel | 'all'>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [autoScroll, setAutoScroll] = useState(true)
  const scrollRef = useRef<HTMLDivElement>(null)
  const endRef = useRef<HTMLDivElement>(null)

  const filtered = filter === 'all'
    ? consoleLog
    : consoleLog.filter((e) => e.level === filter)

  // Reverse for newest-first display
  const reversed = [...filtered].reverse()

  // Auto-scroll when new entries arrive (unless user scrolled up)
  const prevLengthRef = useRef(consoleLog.length)
  useEffect(() => {
    if (consoleLog.length > prevLengthRef.current && autoScroll && endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' })
    }
    prevLengthRef.current = consoleLog.length
  }, [consoleLog.length, autoScroll])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    // If user scrolled to top (since list is reversed, top = newest)
    const atTop = el.scrollTop < 10
    setAutoScroll(atTop)
  }, [])

  return (
    <div className="flex flex-col h-full">
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0">
        <span className="text-xs text-muted-foreground">{consoleLog.length} entries</span>
        <div className="flex items-center gap-1">
          {LEVEL_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${
                filter === f.id
                  ? 'bg-muted text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
          <div className="w-px h-3 bg-border mx-1" />
          <button
            onClick={clearConsoleLog}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Log entries */}
      <div className="flex-1 overflow-auto" ref={scrollRef} onScroll={handleScroll}>
        {!runtime && (
          <p className="text-xs text-muted-foreground px-3 py-2">
            Open an app preview to see console output.
          </p>
        )}
        {runtime && reversed.length === 0 && (
          <p className="text-xs text-muted-foreground px-3 py-2">
            No console entries yet. Interact with the preview.
          </p>
        )}
        {reversed.map((entry) => (
          <div
            key={entry.id}
            className="border-b border-border/50 px-3 py-1 hover:bg-muted/30 cursor-pointer"
            onClick={() => entry.detail != null ? setExpandedId(expandedId === entry.id ? null : entry.id) : undefined}
          >
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground font-mono shrink-0">{formatTime(entry.timestamp)}</span>
              <LevelIcon level={entry.level} />
              <span className="text-[10px] text-muted-foreground/70 font-mono shrink-0 w-10">[{entry.source}]</span>
              <span className={`font-mono truncate ${levelTextColor(entry.level)}`}>{entry.message}</span>
            </div>
            {expandedId === entry.id && entry.detail != null && (
              <div className="mt-1 pl-2 border-l-2 border-muted">
                <JsonTree data={entry.detail} name="detail" />
              </div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>
    </div>
  )
}
